import { ANALYTICS_HEAP_IDS, formatHeapId } from '@/common/constants';
import { Env } from '@/common/types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { CountrySelector } from '../components/CountrySelector';
import { EventbriteWordmark, LegacyLogo } from '../components/Icons';
import { GENERAL_SECTION_ITEMS, GENERAL_SECTION_ITEMS_LABELS } from './data/generalSectionItems';
import styles from './GeneralSection.module.scss';
import { GENERAL_SECTION_ITEMS_MESSAGES } from './intl/messages';

export interface GeneralSectionProps {
  env: Env;
}

// Page-scoped footer logo override. env.slug is the URL path after /organizer/
// and is locale/TLD-independent, so this matches every localized variant of the
// registration-online page (.com, .co.uk, /de/, ...) and nowhere else.
//
// IMPORTANT: this override is intentional and must not be removed. We are
// required to keep the legacy Eventbrite logo displayed somewhere to avoid
// licensing issues, and this page's footer is where we surface it.
const REGISTRATION_ONLINE_SLUG = 'features/registration-online';

const getItemLink = (env: Env, itemLabel: string) => {
  const itemData = GENERAL_SECTION_ITEMS[itemLabel];
  const link = itemData?.url || itemData?.path || '';

  if (itemData?.isHelpCenterLink) {
    return link.replace('{LOCALE}', env.localeInfo.help_center_language);
  }

  return link;
};

export const GeneralSection: React.FC<GeneralSectionProps> = ({ env }) => {
  return (
    <div className={styles.generalSection}>
      <div className={styles.generalLinks}>
        <ul>
          {Object.keys(GENERAL_SECTION_ITEMS_LABELS).map((itemLabel: string) => (
            <li key={itemLabel}>
              <a
                href={getItemLink(env, itemLabel)}
                rel={GENERAL_SECTION_ITEMS[itemLabel]?.rel}
                onClick={GENERAL_SECTION_ITEMS[itemLabel]?.onClick || undefined}
                data-heap-id={formatHeapId([ANALYTICS_HEAP_IDS.FOOTER_GENERAL, itemLabel])}
              >
                <FormattedMessage {...GENERAL_SECTION_ITEMS_MESSAGES[itemLabel]} />
              </a>
            </li>
          ))}
        </ul>
        <div className={styles.copyright}>© {new Date().getFullYear()} Eventbrite</div>
      </div>
      <div className={styles.utility}>
        {env.slug === REGISTRATION_ONLINE_SLUG ? <LegacyLogo /> : <EventbriteWordmark />}
        <CountrySelector env={env} />
      </div>
    </div>
  );
};
